import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Music2, Users, CalendarDays, AlertTriangle, Heart, UserPlus, ArrowUpRight, TrendingUp } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function AdminOverview() {
    const [data, setData] = useState(null);
    useEffect(() => { endpoints.adminOverview().then(setData).catch(() => setData({ error: true })); }, []);

    if (!data) return <div className="p-8 font-mono text-xs text-[#666]" data-testid="admin-overview-loading">Loading…</div>;
    if (data.error) return <div className="p-8 text-sm text-[#ff3333]">Could not load platform overview.</div>;
    const m = data.metrics;
    return (
        <div className="pm-fade" data-testid="admin-overview">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Platform Admin</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Overview</h1>
            </header>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Metric icon={Music2} label="Artists" value={m.total_artists} sub={`${m.active_artists} active`} testId="metric-artists" />
                <Metric icon={Users} label="Users" value={m.total_users} sub={`${m.total_fans} fans`} testId="metric-users" />
                <Metric icon={CalendarDays} label="Events" value={m.upcoming_events + m.completed_events} sub={`${m.upcoming_events} upcoming`} testId="metric-events" />
                <Metric icon={Heart} label="Registrations" value={m.total_registrations} sub={`${m.total_checkins} checked in`} testId="metric-regs" />
                <Metric icon={UserPlus} label="New users 30d" value={m.new_users_30d} testId="metric-new-users" />
                <Metric icon={TrendingUp} label="New follows 30d" value={m.new_followers_30d} testId="metric-new-follows" />
                <Metric icon={AlertTriangle} label="Hidden events" value={m.hidden_events} testId="metric-hidden-events" />
                <Metric icon={CalendarDays} label="Completed" value={m.completed_events} testId="metric-completed" />
            </section>

            {data.attention.length > 0 && (
                <section className="border-b border-[#2a2a2a] px-4 md:px-8 py-6" data-testid="admin-attention">
                    <div className="text-[10px] font-mono uppercase text-[#ff4400]">Needs attention</div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {data.attention.map((a, i) => (
                            <Link key={i} to={a.path.replace("/admin", "/admin").split("?")[0]} className="flex items-center gap-2 border border-[#2a2a2a] p-3 hover:border-[#ff4400] text-sm">
                                <AlertTriangle className="w-4 h-4 text-[#ff4400]" />
                                <span className="flex-1">{a.label}</span>
                                <ArrowUpRight className="w-4 h-4 text-[#666]" />
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2a2a2a]">
                <Panel title="Recent artists" link="/admin/artists" testId="panel-recent-artists">
                    {data.recent_artists.map((a) => (
                        <Row key={a.id} title={a.name} sub={`${a.genre} · ${a.city}, ${a.country}`} testId={`recent-artist-${a.id}`} />
                    ))}
                </Panel>
                <Panel title="Recent sign-ups" link="/admin/users" testId="panel-recent-users">
                    {data.recent_users.map((u) => (
                        <Row key={u.id} title={u.name || u.email} sub={`${u.role} · ${u.email}`} testId={`recent-user-${u.id}`} />
                    ))}
                </Panel>
                <Panel title="Upcoming events" link="/admin/events" testId="panel-upcoming">
                    {data.upcoming_events.map((e) => (
                        <Row key={e.id} title={e.name} sub={`${formatDate(e.date)} · ${e.city}`} testId={`recent-event-${e.id}`} />
                    ))}
                </Panel>
            </div>
        </div>
    );
}

function Metric({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]"><Icon className="w-3 h-3" /> {label}</div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">{value?.toLocaleString?.() ?? value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1">{sub}</div>}
        </div>
    );
}

function Panel({ title, link, testId, children }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{title}</div>
                {link && <Link to={link} className="text-[10px] font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]">All →</Link>}
            </div>
            <div className="mt-3 border border-[#2a2a2a]">
                {children || <div className="p-3 text-xs text-[#a3a3a3]">Nothing here yet.</div>}
            </div>
        </div>
    );
}

function Row({ title, sub, testId }) {
    return (
        <div className="p-3 border-b border-[#2a2a2a] last:border-b-0" data-testid={testId}>
            <div className="font-display font-bold uppercase text-sm truncate">{title}</div>
            <div className="text-[10px] font-mono uppercase text-[#666] truncate">{sub}</div>
        </div>
    );
}
